"use client";

import { c as _c } from "react/compiler-runtime";
import { publicEnv } from "@/config/env";
import { MEMES_CONTRACT } from "@/constants";
import { useSetTitle } from "@/contexts/TitleContext";
import { DBResponse } from "@/entities/IDBResponse";
import { NFTWithMemesExtendedData, VolumeType } from "@/entities/INFT";
import { MemeSeason } from "@/entities/ISeason";
import { SortDirection } from "@/entities/ISort";
import { MemeLabSort, MEMES_EXTENDED_SORT, MemesSort } from "@/enums";
import { numberWithCommas, printMintDate } from "@/helpers/Helpers";
import { fetchUrl } from "@/services/6529api";
import { commonApiFetch } from "@/services/api/common-api";
import { faChevronCircleDown, faChevronCircleUp } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { useContext, useEffect, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { AuthContext } from "../auth/Auth";
import CollectionsDropdown from "../collections-dropdown/CollectionsDropdown";
import DotLoader from "../dotLoader/DotLoader";
import { LFGButton } from "../lfg-slideshow/LFGSlideshow";
import NFTImage from "../nft-image/NFTImage";
import SeasonsDropdown from "../seasons-dropdown/SeasonsDropdown";
import { VolumeTypeDropdown } from "./MemeShared";
import styles from "./TheMemes.module.scss";
interface Meme {
  meme: number;
  meme_name: string;
}
const MEMES_SORT_TO_API: Record<MemesSort, string> = {
  [MemesSort.AGE]: MEMES_EXTENDED_SORT[0],
  [MemesSort.EDITION_SIZE]: MEMES_EXTENDED_SORT[1],
  [MemesSort.MEME]: MEMES_EXTENDED_SORT[2],
  [MemesSort.HODLERS]: MEMES_EXTENDED_SORT[3],
  [MemesSort.TDH]: MEMES_EXTENDED_SORT[4],
  [MemesSort.UNIQUE_PERCENT]: MEMES_EXTENDED_SORT[5],
  [MemesSort.UNIQUE_PERCENT_EX_MUSEUM]: MEMES_EXTENDED_SORT[6],
  [MemesSort.FLOOR_PRICE]: MEMES_EXTENDED_SORT[7],
  [MemesSort.MARKET_CAP]: MEMES_EXTENDED_SORT[8],
  [MemesSort.VOLUME]: MEMES_EXTENDED_SORT[12],
  [MemesSort.HIGHEST_OFFER]: MEMES_EXTENDED_SORT[13]
};
const API_SORT_TO_MEMES: Record<string, {
  sort: MemesSort;
  volume?: VolumeType;
}> = {
  [MEMES_EXTENDED_SORT[0]]: {
    sort: MemesSort.AGE
  },
  [MEMES_EXTENDED_SORT[1]]: {
    sort: MemesSort.EDITION_SIZE
  },
  [MEMES_EXTENDED_SORT[2]]: {
    sort: MemesSort.MEME
  },
  [MEMES_EXTENDED_SORT[3]]: {
    sort: MemesSort.HODLERS
  },
  [MEMES_EXTENDED_SORT[4]]: {
    sort: MemesSort.TDH
  },
  [MEMES_EXTENDED_SORT[5]]: {
    sort: MemesSort.UNIQUE_PERCENT
  },
  [MEMES_EXTENDED_SORT[6]]: {
    sort: MemesSort.UNIQUE_PERCENT_EX_MUSEUM
  },
  [MEMES_EXTENDED_SORT[7]]: {
    sort: MemesSort.FLOOR_PRICE
  },
  [MEMES_EXTENDED_SORT[8]]: {
    sort: MemesSort.MARKET_CAP
  },
  [MEMES_EXTENDED_SORT[9]]: {
    sort: MemesSort.VOLUME,
    volume: VolumeType.HOURS_24
  },
  [MEMES_EXTENDED_SORT[10]]: {
    sort: MemesSort.VOLUME,
    volume: VolumeType.DAYS_7
  },
  [MEMES_EXTENDED_SORT[11]]: {
    sort: MemesSort.VOLUME,
    volume: VolumeType.DAYS_30
  },
  [MEMES_EXTENDED_SORT[12]]: {
    sort: MemesSort.VOLUME,
    volume: VolumeType.ALL_TIME
  },
  [MEMES_EXTENDED_SORT[13]]: {
    sort: MemesSort.HIGHEST_OFFER
  }
};
function getApiSort(sort: MemesSort, volumeType: VolumeType): string {
  if (sort === MemesSort.VOLUME) {
    switch (volumeType) {
      case VolumeType.HOURS_24:
        return MEMES_EXTENDED_SORT[9];
      case VolumeType.DAYS_7:
        return MEMES_EXTENDED_SORT[10];
      case VolumeType.DAYS_30:
        return MEMES_EXTENDED_SORT[11];
      default:
        return MEMES_EXTENDED_SORT[12];
    }
  }
  return MEMES_SORT_TO_API[sort];
}
export default function TheMemesComponent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const {
    connectedProfile
  } = useContext(AuthContext);
  const [selectedSeason, setSelectedSeason] = useState(0);
  const [seasons, setSeasons] = useState<MemeSeason[]>([]);
  const [routerLoaded, setRouterLoaded] = useState(false);
  useSetTitle("The Memes | Collections");
  useEffect(() => {
    let initialSortDir = SortDirection.ASC;
    let initialSort = MemesSort.AGE;
    let initialVolume = VolumeType.ALL_TIME;
    let initialSzn = 0;
    const routerSortDir = searchParams?.get("sort_dir");
    if (routerSortDir) {
      const resolvedRouterSortDir = Object.values(SortDirection).find(sd => sd.toLowerCase() === routerSortDir.toLowerCase());
      if (resolvedRouterSortDir) {
        initialSortDir = resolvedRouterSortDir;
      }
    }
    const routerSort = searchParams?.get("sort");
    if (routerSort) {
      const sortLower = routerSort.toLowerCase();
      if (sortLower.startsWith("volume_")) {
        initialSort = MemesSort.VOLUME;
        const volKey = sortLower.replace("volume_", "").toUpperCase();
        const volMatch = Object.keys(VolumeType).find(k => k.toLowerCase() === volKey.toLowerCase());
        if (volMatch) {
          initialVolume = VolumeType[volMatch as keyof typeof VolumeType];
        }
      } else {
        const resolvedKey = Object.keys(MemesSort).find(k_0 => k_0.toLowerCase() === sortLower);
        if (resolvedKey) {
          initialSort = MemesSort[resolvedKey as keyof typeof MemesSort];
        }
      }
    }
    const routerSzn = searchParams?.get("szn");
    if (routerSzn) {
      if (Array.isArray(routerSzn)) {
        initialSzn = parseInt(routerSzn[0]);
      } else {
        initialSzn = parseInt(routerSzn);
      }
    }
    setSort(initialSort);
    setSortDir(initialSortDir);
    setSelectedSeason(initialSzn);
    setVolumeType(initialVolume);
    setRouterLoaded(true);
  }, [searchParams]);
  const getNftsNextPage = () => {
    const mySort = getApiSort(sort, volumeType);
    let seasonFilter = "";
    if (selectedSeason > 0) {
      seasonFilter = `&season=${selectedSeason}`;
    }
    return `${publicEnv.API_ENDPOINT}/api/memes_extended_data?page_size=48&sort_direction=${sortDir}&sort=${mySort}${seasonFilter}`;
  };
  const [sortDir, setSortDir] = useState<SortDirection>(SortDirection.ASC);
  const [sort, setSort] = useState<MemesSort>(MemesSort.AGE);
  const [volumeType, setVolumeType] = useState<VolumeType>(VolumeType.ALL_TIME);
  const [fetching, setFetching] = useState(true);
  const [nfts, setNfts] = useState<NFTWithMemesExtendedData[]>([]);
  const [nftsNextPage, setNftsNextPage] = useState<string>();
  const [nftMemes, setNftMemes] = useState<Meme[]>([]);
  const [nftsByMeme, setNftsByMeme] = useState<Map<number, NFTWithMemesExtendedData[]>>(new Map());
  useEffect(() => {
    commonApiFetch<MemeSeason[]>({
      endpoint: "new_memes_seasons"
    }).then(response => {
      setSeasons(response);
    });
  }, []);
  useEffect(() => {
    let sortParam: string;
    if (sort === MemesSort.VOLUME) {
      const volKey_0 = Object.entries(VolumeType).find(([key, value]) => value === volumeType)?.[0];
      if (volKey_0) {
        sortParam = `volume_${volKey_0.toLowerCase()}`;
      } else {
        sortParam = "volume_all_time"; // fallback
      }
    } else {
      const found = Object.entries(MemesSort).find(([key_0, value_0]) => value_0 === sort);
      sortParam = found ? found[0].toLowerCase() : sort.toLowerCase();
    }
    let queryString = `sort=${sortParam}&sort_dir=${sortDir.toLowerCase()}`;
    if (selectedSeason > 0) {
      queryString += `&szn=${selectedSeason}`;
    }
    router.push(`the-memes?${queryString}`);
  }, [sort, sortDir, selectedSeason, volumeType]);
  useEffect(() => {
    const memesMap = new Map<number, {
      meme: Meme;
      items: NFTWithMemesExtendedData[];
    }>();
    for (const nft of nfts) {
      const existing = memesMap.get(nft.meme);
      if (existing) {
        existing.items.push(nft);
      } else {
        memesMap.set(nft.meme, {
          meme: {
            meme: nft.meme,
            meme_name: nft.meme_name
          },
          items: [nft]
        });
      }
    }
    const sortedMemes = Array.from(memesMap.values()).map(value_1 => value_1.meme);
    if (sortDir === SortDirection.DESC) {
      sortedMemes.sort((a, b) => b.meme - a.meme);
    } else {
      sortedMemes.sort((a_0, b_0) => a_0.meme - b_0.meme);
    }
    setNftMemes(sortedMemes);
    const nftsByMeme_0 = new Map<number, NFTWithMemesExtendedData[]>();
    for (const [key_1, {
      items
    }] of Array.from(memesMap.entries())) {
      nftsByMeme_0.set(key_1, items.toSorted((a_1, b_1) => a_1.id - b_1.id));
    }
    setNftsByMeme(nftsByMeme_0);
  }, [nfts, sortDir]);
  function fetchNfts() {
    if (!nftsNextPage) {
      setFetching(false);
      return;
    }
    fetchUrl(nftsNextPage).then((responseNfts: DBResponse) => {
      setNfts(prev => [...prev, ...(responseNfts.data ?? [])]);
      setNftsNextPage(responseNfts.next);
    }).catch(() => {
      // optionally surface a toast/log here
    }).finally(() => setFetching(false));
  }
  useEffect(() => {
    if (routerLoaded) {
      setNfts([]);
      setNftsNextPage(getNftsNextPage());
      setFetching(true);
    }
  }, [sort, sortDir, volumeType, selectedSeason, routerLoaded]);
  useEffect(() => {
    if (fetching && routerLoaded && nftsNextPage) {
      fetchNfts();
    }
  }, [fetching, routerLoaded, nftsNextPage]);
  useEffect(() => {
    if (!nftsNextPage) {
      return;
    }
    let throttleTimeout: ReturnType<typeof setTimeout> | null = null;
    const handleScroll = () => {
      if (throttleTimeout) {
        return;
      }
      throttleTimeout = setTimeout(() => {
        throttleTimeout = null;
        const distanceFromBottom = document.documentElement.scrollHeight - window.innerHeight - window.scrollY;
        if (distanceFromBottom <= 400 && routerLoaded) {
          setFetching(true);
        }
      }, 200);
    };
    window.addEventListener("scroll", handleScroll);
    return () => {
      if (throttleTimeout) {
        clearTimeout(throttleTimeout);
      }
      window.removeEventListener("scroll", handleScroll);
    };
  }, [routerLoaded, nftsNextPage]);
  function getVolume(nft_0: NFTWithMemesExtendedData) {
    let vol = 0;
    switch (volumeType) {
      case VolumeType.HOURS_24:
        vol = nft_0.total_volume_last_24_hours;
        break;
      case VolumeType.DAYS_7:
        vol = nft_0.total_volume_last_7_days;
        break;
      case VolumeType.DAYS_30:
        vol = nft_0.total_volume_last_1_month;
        break;
      case VolumeType.ALL_TIME:
        vol = nft_0.total_volume;
        break;
    }
    if (vol > 0) {
      return `${numberWithCommas(Math.round(vol * 100) / 100)} ETH`;
    }
    return "-";
  }
  function printNft(nft_1: NFTWithMemesExtendedData) {
    return <Col key={`${nft_1.contract}-${nft_1.id}`} className="pt-3 pb-3" xs={{
      span: 6
    }} sm={{
      span: 4
    }} md={{
      span: 3
    }} lg={{
      span: 3
    }}>
        <Link href={`/the-memes/${nft_1.id}`} className="decoration-none scale-hover">
          <Container fluid>
            <Row className={connectedProfile ? styles.nftImagePadding : ""}>
              <NFTImage nft={nft_1} animation={false} height={300} showThumbnail={true} showBalance={true} />
            </Row>
            <Row>
              <Col className="text-center pt-2">
                #{nft_1.id} - {nft_1.name}
              </Col>
            </Row>
            <Row>
              <Col className="text-center pt-1">
                {sort && (sort === MemesSort.AGE || sort === MemesSort.MEME) && printMintDate(nft_1.mint_date)}
                {sort === MemesSort.EDITION_SIZE && `Edition Size: ${numberWithCommas(nft_1.supply)}`}
                {sort === MemesSort.TDH && `TDH: ${numberWithCommas(Math.round(nft_1.boosted_tdh))}`}
                {sort === MemesSort.HODLERS && `Collectors: ${numberWithCommas(nft_1.hodlers)}`}
                {sort === MemesSort.UNIQUE_PERCENT && `Unique: ${Math.round(nft_1.percent_unique * 100 * 10) / 10}%`}
                {sort === MemesSort.UNIQUE_PERCENT_EX_MUSEUM && `Unique Ex-Museum: ${Math.round(nft_1.percent_unique_cleaned * 100 * 10) / 10}%`}
                {sort === MemesSort.FLOOR_PRICE && (nft_1.floor_price > 0 ? `Floor Price: ${numberWithCommas(Math.round(nft_1.floor_price * 1000) / 1000)} ETH` : `Floor Price: N/A`)}
                {sort === MemesSort.HIGHEST_OFFER && (nft_1.highest_offer > 0 ? `Highest Offer: ${numberWithCommas(Math.round(nft_1.highest_offer * 1000) / 1000)} ETH` : `Highest Offer: N/A`)}
                {sort === MemesSort.MARKET_CAP && (nft_1.market_cap > 0 ? `Market Cap: ${numberWithCommas(Math.round(nft_1.market_cap * 100) / 100)} ETH` : `Market Cap: N/A`)}
                {sort === MemesSort.VOLUME && `Volume (${volumeType}): ${getVolume(nft_1)}`}
              </Col>
            </Row>
          </Container>
        </Link>
      </Col>;
  }
  function printNfts() {
    return <Row className="pt-2">{nfts.map(nft_2 => printNft(nft_2))}</Row>;
  }
  function getNftsForMeme(meme: Meme) {
    return nftsByMeme.get(meme.meme) ?? [];
  }
  function printMemes() {
    return nftMemes.map(meme_0 => {
      const memeNfts = getNftsForMeme(meme_0);
      return <Row key={`${meme_0.meme}-${meme_0.meme_name}`}>
          <Col xs={12} className="pt-3">
            <Col>
              <h4 className="font-color">
                {meme_0.meme} - {meme_0.meme_name}
              </h4>
            </Col>
          </Col>
          {memeNfts.map(nft_3 => printNft(nft_3))}
        </Row>;
    });
  }
  return <Container fluid className={styles.mainContainer}>
      <Row>
        <Col>
          <Container className="pt-4">
            <>
              {/* Page header - visible on all devices */}
              <Row>
                <Col className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                  <span className="d-flex align-items-center gap-3 flex-wrap">
                    <h1 className="no-wrap mb-0">
                      The Memes
                    </h1>
                    <LFGButton contract={MEMES_CONTRACT} />
                  </span>
                  <div className="d-none d-sm-block">
                    <SeasonsDropdown seasons={seasons.map(s => s.id)} selectedSeason={selectedSeason} setSelectedSeason={setSelectedSeason} />
                  </div>
                </Col>
              </Row>

              {/* Mobile & tablet elements - visible until xl breakpoint (1200px) */}
              <Row className="d-xl-none">
                <Col xs={12} className="mb-3">
                  <Row>
                    <Col xs={12} sm="auto">
                      <CollectionsDropdown activePage="memes" />
                    </Col>
                  </Row>
                </Col>
                <Col xs={12} className="mb-3 d-flex d-sm-none">
                  <div className="text-start">
                    <SeasonsDropdown seasons={seasons.map(s_0 => s_0.id)} selectedSeason={selectedSeason} setSelectedSeason={setSelectedSeason} />
                  </div>
                </Col>
              </Row>
              <Row className="pt-2">
                <Col>
                  Sort by&nbsp;&nbsp;
                  <FontAwesomeIcon icon={faChevronCircleUp} onClick={() => setSortDir(SortDirection.ASC)} width={16} color={sortDir != SortDirection.ASC ? "#9a9a9a" : "#fff"} cursor={"pointer"} className={styles.sortDirection} />{" "}
                  <FontAwesomeIcon icon={faChevronCircleDown} onClick={() => setSortDir(SortDirection.DESC)} width={16} color={sortDir != SortDirection.DESC ? "#9a9a9a" : "#fff"} cursor={"pointer"} className={styles.sortDirection} />
                </Col>
              </Row>
              <Row className="pt-2">
                <Col className="tw-flex tw-gap-3 tw-items-center tw-flex-wrap">
                  {Object.values(MemesSort).filter(v => v != MemesSort.VOLUME).map(v_0 => <SortButton key={v_0} currentSort={sort} sort={v_0} select={() => setSort(v_0)} />)}
                  {printVolumeTypeDropdown(sort === MemesSort.VOLUME, setVolumeType, () => setSort(MemesSort.VOLUME), volumeType)}
                </Col>
              </Row>
              {nfts.length > 0 && sort === MemesSort.MEME ? printMemes() : printNfts()}
              {fetching && nftsNextPage && <Row>
                  <Col className="pt-3 pb-5">
                    Fetching <DotLoader />
                  </Col>
                </Row>}
            </>
          </Container>
        </Col>
      </Row>
    </Container>;
}
export function SortButton(props) {
  const $ = _c(6);
  const isActive = props.currentSort === props.sort;
  let t0;
  if ($[0] !== props) {
    t0 = () => props.select();
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const t1 = `tw-bg-transparent tw-border-none tw-p-0 tw-m-0 tw-no-underline tw-cursor-pointer tw-transition-colors tw-duration-200 ${isActive ? "tw-text-white tw-font-semibold" : "tw-text-gray-400 hover:tw-text-white"}`;
  let t2;
  if ($[2] !== props.sort || $[3] !== t0 || $[4] !== t1) {
    t2 = <button type="button" onClick={t0} className={t1}>{props.sort}</button>;
    $[2] = props.sort;
    $[3] = t0;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}
export function printVolumeTypeDropdown(isVolumeSort: boolean, setVolumeType: (volumeType: VolumeType) => void, setVolumeSort: () => void, selectedVolumeSort: VolumeType = VolumeType.ALL_TIME) {
  return <VolumeTypeDropdown isVolumeSort={isVolumeSort} selectedVolumeSort={selectedVolumeSort} setVolumeType={setVolumeType} setVolumeSort={setVolumeSort} />;
}